/** Normalize Users/GetAll and recommendation-shaped payloads into flat user records */
export function normalizeUsersList(data) {
  if (!data) return [];
  const list = Array.isArray(data) ? data : data.users || data.items || [];
  return list
    .map((item) => {
      if (!item) return null;
      if (item.user && typeof item.user === "object") return { ...item.user, _distance: item.distance };
      return item;
    })
    .filter(Boolean);
}

function createdAtMs(user) {
  const raw = user?.createdAt ?? user?.created_at ?? null;
  if (!raw) return 0;
  const ms = new Date(raw).getTime();
  return Number.isFinite(ms) ? ms : 0;
}

/** Newest profiles first (createdAt desc). Stable fallback by id. */
export function sortUsersByNewest(users) {
  return [...(users || [])].sort((a, b) => {
    const diff = createdAtMs(b) - createdAtMs(a);
    if (diff !== 0) return diff;
    return String(b?.id || "").localeCompare(String(a?.id || ""));
  });
}

export function userDisplayName(user) {
  if (!user) return "—";
  const name = [user.firstName, user.lastName].filter(Boolean).join(" ").trim();
  return name || user.email || user.id || "—";
}

export function userSearchHaystack(user) {
  return [
    user.id,
    user.firstName,
    user.lastName,
    user.email,
    user.country,
    user.occupation,
    user.education,
    user.gender,
    user.preferences?.gender,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

/** Prefer preferences.gender (Discover), then flat gender from mapProfile. */
export function formatUserGender(user) {
  const raw = user?.preferences?.gender ?? user?.gender ?? "";
  const value = String(raw).trim();
  if (!value) return "—";
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
}

function isBlank(value) {
  if (value == null) return true;
  if (typeof value === "number") return !Number.isFinite(value);
  return String(value).trim() === "";
}

function hasUsableProfileImage(user) {
  const uri = user?.profileImageUri;
  if (isBlank(uri)) return false;
  const s = String(uri);
  if (s.includes("ui-avatars.com")) return false;
  if (s.includes("broken") || s === "null" || s === "undefined") return false;
  return true;
}

/** Filters for admin Users table: missing fields / status. Keys are filter ids. */
export const ADMIN_PROFILE_FILTERS = [
  { id: "missingGender", label: "Gender", group: "gaps", test: (u) => isBlank(u.preferences?.gender ?? u.gender) },
  { id: "missingCountry", label: "Country", group: "gaps", test: (u) => isBlank(u.country) },
  { id: "missingBio", label: "Bio", group: "gaps", test: (u) => isBlank(u.bio) },
  { id: "missingPhotos", label: "Photos", group: "gaps", test: (u) => !hasUsableProfileImage(u) },
  { id: "missingAge", label: "Age", group: "gaps", test: (u) => isBlank(u.age) },
  { id: "missingOccupation", label: "Occupation", group: "gaps", test: (u) => isBlank(u.occupation) },
  { id: "missingEducation", label: "Education", group: "gaps", test: (u) => isBlank(u.education) },
  {
    id: "missingLifestyle",
    label: "Lifestyle",
    group: "gaps",
    test: (u) =>
      isBlank(u.drinking) && isBlank(u.smoking) && isBlank(u.workout) && isBlank(u.religion),
  },
  { id: "incomplete", label: "Incomplete", group: "status", test: (u) => !u.isProfileComplete },
  {
    id: "unverified",
    label: "Unverified",
    group: "status",
    test: (u) => !(u.isVerified ?? u.verified),
  },
];

export const ADMIN_FILTER_GROUPS = [
  { id: "gaps", title: "Missing fields", hint: "Profiles with empty data" },
  { id: "status", title: "Status", hint: "Completion & verification" },
];

export function countUsersForFilter(users, filterId) {
  const def = ADMIN_PROFILE_FILTERS.find((f) => f.id === filterId);
  if (!def || !users?.length) return 0;
  return users.reduce((n, u) => n + (def.test(u) ? 1 : 0), 0);
}

export function userMatchesProfileFilters(user, activeFilterIds) {
  if (!activeFilterIds?.length) return true;
  return activeFilterIds.every((id) => {
    const def = ADMIN_PROFILE_FILTERS.find((f) => f.id === id);
    return def ? def.test(user) : true;
  });
}
